/**
 * 更新胶囊 — 监测类页面头部右侧的信任标识（视觉基线 §4.2）。
 *
 * 结构：状态点（ok=绿 / warn=黄 / info=蓝）+ "数据更新至 MM-DD HH:mm · n=X 条"。
 * 样式全部使用 theme token，胶囊底取 semanticBg.neutral。
 */
import { Box, Typography } from "@mui/material";

export interface UpdateCapsuleProps {
  /** 数据最近更新时间（ISO 字符串或 Date）。 */
  updatedAt: string | Date;
  /** 样本量（可选，如 n=30 条）。 */
  sampleN?: number;
  /** 状态点语义，默认 ok。 */
  status?: "ok" | "warn" | "info";
  /** 样本量单位，默认"条"。 */
  sampleUnit?: string;
}

/** 状态点颜色（theme 语义色）。 */
const DOT_COLOR: Record<NonNullable<UpdateCapsuleProps["status"]>, string> = {
  ok: "success.main",
  warn: "warning.main",
  info: "info.main",
};

/** 格式化胶囊时间（MM-DD HH:mm）。 */
function formatCapsuleTime(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}-${dd} ${hh}:${mi}`;
}

export default function UpdateCapsule({
  updatedAt,
  sampleN,
  status = "ok",
  sampleUnit = "条",
}: UpdateCapsuleProps) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.25,
        borderRadius: 999,
        bgcolor: "semanticBg.neutral",
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: DOT_COLOR[status],
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
        数据更新至 {formatCapsuleTime(updatedAt)}
        {sampleN !== undefined && ` · n=${sampleN} ${sampleUnit}`}
      </Typography>
    </Box>
  );
}
